import React, { useState, useEffect } from 'react';
import { Clock, Save, Plus, Trash2, AlertTriangle, Info } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { otaTimerAPI } from '../../services/api';
import { OTA_NAME_OPTIONS } from '../../constants/dropdownOptions';

const OTATimerSettings = () => {
  const { user } = useAuth();
  const [limits, setLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  const otaOptions = OTA_NAME_OPTIONS.filter(o => o.value !== '');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await otaTimerAPI.getTimeLimits();
        setLimits(res.data || []);
      } catch (e) {
        console.error('Error loading OTA time limits:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAdd = () => {
    const usedNames = limits.map(l => l.ota_name);
    const nextOTA = otaOptions.find(o => !usedNames.includes(o.value));
    if (!nextOTA) return;
    setLimits(prev => [...prev, { ota_name: nextOTA.value, time_limit_minutes: 60 }]);
  };

  const handleRemove = (index) => {
    setLimits(prev => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    setLimits(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await otaTimerAPI.updateTimeLimits(limits);
      setMessage('OTA time limits saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      setMessage(e.response?.status === 403 ? 'Only admins can change this setting' : 'Error saving settings');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  const usedNames = limits.map(l => l.ota_name);
  const canAdd = otaOptions.some(o => !usedNames.includes(o.value));

  return (
    <div className="space-y-6 max-w-2xl" data-testid="ota-timer-settings">
      <div>
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Clock className="text-teal-600" size={24} />
          OTA Timer Limits
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Set the maximum allowed time for each OTA listing. When a running timer exceeds the limit, admin receives notifications every 15 minutes until the timer is stopped.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        {limits.length === 0 && (
          <div className="p-6 text-center text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <Clock size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">No OTA time limits configured yet.</p>
            <p className="text-xs mt-1">OTAs without a configured limit will use the default of 60 minutes.</p>
          </div>
        )}

        {limits.map((item, index) => (
          <div
            key={index}
            className="flex items-end gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
            data-testid={`ota-limit-row-${index}`}
          >
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">OTA Platform</label>
              <select
                value={item.ota_name}
                onChange={(e) => handleChange(index, 'ota_name', e.target.value)}
                disabled={!isAdmin}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                data-testid={`ota-limit-name-${index}`}
              >
                {otaOptions
                  .filter(o => o.value === item.ota_name || !usedNames.includes(o.value))
                  .map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
              </select>
            </div>
            <div className="w-44">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Time Limit (minutes)</label>
              <input
                type="number"
                value={item.time_limit_minutes}
                onChange={(e) => handleChange(index, 'time_limit_minutes', parseInt(e.target.value) || 60)}
                min={1}
                max={10080}
                disabled={!isAdmin}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                data-testid={`ota-limit-minutes-${index}`}
              />
            </div>
            {isAdmin && (
              <button
                onClick={() => handleRemove(index)}
                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                data-testid={`ota-limit-remove-${index}`}
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}

        {isAdmin && canAdd && (
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 text-teal-600 hover:text-teal-700 text-sm font-medium mt-2"
            data-testid="add-ota-limit-btn"
          >
            <Plus size={16} /> Add OTA Time Limit
          </button>
        )}

        {!isAdmin && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-700">Only administrators can modify OTA timer settings.</p>
          </div>
        )}

        {isAdmin && (
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-ota-limits-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors font-medium text-sm"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save OTA Limits'}
          </button>
        )}

        {message && (
          <p className={`text-sm font-medium ${message.includes('success') ? 'text-green-600' : 'text-red-600'}`}>{message}</p>
        )}
      </div>

      <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Info size={16} className="text-gray-500" />
          How OTA Timers Work
        </h4>
        <div className="space-y-2">
          {[
            'Start a timer when OTA listing work begins for a lead',
            'Timer continues running even after exceeding the time limit until Stop is clicked',
            'Admin receives a notification when time limit is exceeded',
            'Notifications repeat every 15 minutes until the timer is stopped',
            'Default time limit is 60 minutes if not configured for an OTA',
          ].map((text, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1.5 flex-shrink-0"></div>
              {text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OTATimerSettings;
